/**
 * O manifesto do PAINEL ADMINISTRATIVO do Lu Barros Studio.
 *
 * O ID E DEFINITIVO: `studiolu-admin` entra em rota, em chave de armazenamento e em registro de
 * auditoria, e trocar depois quebra historico. O loader de `@complexity/product-kit` recusa
 * qualquer formato que nao seja minusculas, numeros e hifen.
 *
 * MIGRACAO REPRODUZ O LEGADO, e cada texto abaixo foi LIDO na tela que esta no ar, e nao
 * escrito para a ocasiao. A origem e `portal/admin.html`, com a linha ao lado de cada campo.
 * O que a tela nova do Chassi pede e o legado nao tem fica VAZIO e dito, e nunca preenchido
 * com texto inventado: ausencia de dado e dita, nunca preenchida.
 *
 * PRIMEIRA ENTREGA: so o login. O menu tem UM item porque o loader recusa menu vazio, e o item
 * e a aba que o legado abre por padrao. As outras sete abas entram quando as telas migrarem.
 */

import type { ProductConfig } from '@complexity/product-kit';

export const studioluAdmin: ProductConfig = {
  id: 'studiolu-admin',

  // O eyebrow que o legado escreve acima do titulo (`admin.html:1713`). No Chassi ele aparece
  // ao lado do simbolo, no menu e na tela de entrada, que e o mesmo lugar de identidade.
  nome: 'Lu Barros Studio',

  // As duas letras do monograma do favicon legado (`admin.html:17`).
  iniciais: 'LB',

  // Um PAPEL de cor, e nao um hex: o valor mora em packages/tokens.
  accent: 'marca',

  login: {
    // O titulo da tela de entrada do legado (`admin.html:1714`), palavra por palavra.
    titulo: 'Painel administrativo',

    // O LEGADO NAO TEM SUBTITULO, NEM PARAGRAFO, NEM SELO na tela de entrada do admin
    // (`admin.html:1726-1742`): so o botao do Google, o divisor e o formulario. Os tres campos
    // sao obrigatorios no contrato, e ficam vazios em vez de ganhar um texto que a tela nunca
    // teve. O limite fica dito: a `TelaLogin` desenha o <p> do subtitulo mesmo vazio, sem
    // altura visivel, porque o componente e copiado e nao se edita aqui.
    subtitulo: '',
    paragrafos: [],
    selos: [],

    // SEM `blocoIA` e SEM `privacidade`: o login do admin legado nao mostra nenhum dos dois.
    // SEM `somenteGoogleEmProducao`: o legado oferece Google E e-mail com senha em producao
    // (`admin.html:1730` e `1732-1740`), e ausente o Chassi mantem as duas formas.
  },

  // O Sair mora no painel da conta, que mostra quem está logado (nome, e-mail, papel): regra global
  // do fundador, e o pedido dele em 19/09/2026 de que o Sair do sistema novo volte ao login novo.
  sairNaConta: true,

  // AS OITO AREAS DO ADMIN ATUAL, na ordem e com os nomes dele (`admin.html:1761-1768`). A comunidade
  // fica fora desta refatoracao, por decisao do fundador em 19/09/2026.
  //
  // O RECORTE POR PAPEL e o de `aplicarRestricoesDePapel` (`admin.html:3425`): cada item declara a
  // permissao que exige, e `apps/admin/src/papeis.ts` diz quais permissoes cada papel tem. Resultado:
  // financeiro ve Vendas e Financeiro; marketing ve so Vendas; consulta ve tudo menos Sistema.
  // OS DOIS GRUPOS NOMEADOS, pedidos pelo fundador em 19/09/2026 e aceitos por enquete às 20:55:
  // "Operação" no topo e "Sistema" no rodapé, os dois SEMPRE VISÍVEIS e sem recolher.
  //
  // NENHUM CAMPO NOVO NASCEU PARA ISSO, e a medição é o motivo: o Chassi já conhece seção de menu
  // (`SecaoMenu = 'operacao' | 'sistema'` em `packages/product-kit/src/types.ts:201`), já guarda o
  // rótulo de cada uma (`ROTULO_SECAO`, linha 223) e já desenha o cabeçalho do grupo na troca de
  // seção (`packages/layout/src/components/Rail.tsx:519-523`). Somar um segundo campo com o mesmo
  // papel produziria duas fontes para a mesma decisão, e a errada seria a que ninguém olha.
  //
  // O CABEÇALHO SÓ APARECE COM MAIS DE UMA SEÇÃO (`Rail.tsx:275`), de propósito: com uma seção só,
  // "Operação" no topo não separa nada de nada. Aqui existem as duas, então os dois títulos
  // desenham, e um grupo que o recorte por papel esvaziar perde o título junto, porque o cabeçalho
  // é desenhado a partir de um item, e não de uma lista fixa.
  //
  // O RECORTE POR PAPEL não mudou: é o de `aplicarRestricoesDePapel` (`admin.html:3425`), com a
  // permissão declarada em cada item e a tabela de papéis em `apps/admin/src/papeis.ts`.
  menu: [
    // ---------------------------------------------------------------------------------------
    // GRUPO "Operação": as oito áreas do dia a dia, na ordem que o fundador escreveu.
    // ---------------------------------------------------------------------------------------
    // O PAINEL, acima de Compromissos, por pedido do fundador em 19/09/2026: um dashboard que leva a
    // cada área do sistema. Não existe no admin atual; é funcionalidade nova, pedida por ele.
    { icone: 'painel', rota: '/painel', label: 'Painel', permissoes: ['painel:ver'], secao: 'operacao' },
    {
      icone: 'agenda',
      rota: '/compromissos',
      label: 'Compromissos',
      permissoes: ['compromissos:ver'],
      secao: 'operacao',
    },
    { icone: 'pessoas', rota: '/clientes', label: 'Clientes', permissoes: ['clientes:ver'], secao: 'operacao' },
    { icone: 'relatorios', rota: '/vendas', label: 'Vendas', permissoes: ['vendas:ver'], secao: 'operacao' },
    {
      icone: 'inventario',
      rota: '/matriz',
      label: 'Matriz de Produtos (Serviços)',
      permissoes: ['matriz:ver'],
      secao: 'operacao',
    },
    {
      icone: 'conversa',
      rota: '/depoimentos',
      label: 'Depoimentos',
      permissoes: ['depoimentos:ver'],
      secao: 'operacao',
    },
    {
      icone: 'financeiro',
      rota: '/financeiro',
      label: 'Financeiro',
      permissoes: ['financeiro:ver'],
      secao: 'operacao',
    },
    {
      icone: 'registros',
      rota: '/arquivos',
      // "REPOSITÓRIO DE ARQUIVOS" desde 07/10/2026, por pedido do fundador: "muda nome do menu
      // arquivos administrativos por Repositório de Arquivos". Só o rótulo muda: a rota, a
      // permissão e a pasta do armazenamento (`admin-arquivos`) continuam as mesmas, e por isso
      // nada do que já está guardado se move.
      label: 'Repositório de Arquivos',
      permissoes: ['arquivos:ver'],
      secao: 'operacao',
    },
    // ---------------------------------------------------------------------------------------
    // GRUPO "Sistema", no rodapé do menu.
    // ---------------------------------------------------------------------------------------
    // "CONFIGURAÇÃO" É O ITEM QUE SE CHAMAVA "SISTEMA". Só o rótulo mudou: a rota, a permissão e as
    // onze sub-abas continuam as mesmas. O nome "Sistema" passou a ser o do GRUPO, e dois nomes
    // iguais em níveis diferentes fariam a pessoa procurar a área dentro dela mesma.
    {
      icone: 'engrenagem',
      rota: '/sistema',
      label: 'Configuração',
      permissoes: ['sistema:ver'],
      secao: 'sistema',
    },
    // A AJUDA É DECLARADA AQUI, e não deixada para o Chassi injetar. O Chassi só injeta o item
    // quando o produto NÃO o declara (`Chassi.tsx:185-186`), e injetado ele entraria no fim da
    // lista sem seção, ou seja, dentro de "Operação". Declarando, ela nasce no grupo certo, e a
    // tela continua sendo a Central de Ajuda do Chassi: aqui não nasce tela nova.
    //
    // SEM `permissoes`: a ajuda é de todo papel, inclusive o de consulta. Item de ajuda escondido
    // de quem tem menos acesso é justamente o contrário do que ele serve.
    { icone: 'ajuda', rota: '/ajuda', label: 'Ajuda', secao: 'sistema' },
  ],

  ajuda: {
    // O namespace e o id do produto: dois produtos com o mesmo namespace veriam a ajuda um do
    // outro, e o da cliente e outro.
    namespace: 'studiolu-admin',
  },

  mensagens: {
    // O ADMIN E O LADO DA OPERACAO: e quem responde a cliente. O plano e `tenant` porque o
    // estudio e um cliente so, e nao existe aqui o operador que atravessa varios clientes.
    lado: 'operacao',
    plano: 'tenant',
  },

  ia: {
    // DESLIGADA NESTA ENTREGA. O legado chama a IA por uma function do Supabase
    // (`admin.html:6170`), e nao pelo `ai-gateway` que o contrato pressupoe. Ligar aqui e
    // decisao da migracao daquela tela, e nao do login. Desligada, a cota e zero por regra
    // do loader.
    habilitada: false,
    cotaMensalTokens: 0,
  },
};

export default studioluAdmin;
